import pickBy from 'lodash/pickBy'

import { SITTER_FAQ_MESSAGE_TYPE } from '@app/constants/Misc'

import { createSelector } from '@app/utils/createSelector'
import { createLocalStorage } from '@app/utils/localStorage'

import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserIdSelector, profileUserResultSelector } from '@app/store/selectors/profile'
import type { ThunkAction } from '@app/store/thunk'

import { sitterFaqChatMessageEnabledResultSelector } from './app_config'

/**
 * Client-injected chat record, persisted per user in localStorage since the
 * Twilio era — in practice a single entry, the sitter-FAQ bubble. Payloads are
 * read back without validation, so this describes only the fields we read; older
 * records carry more (index, channel_sid, date_updated, visible) and are ignored.
 * Field names are the on-disk contract and must not be renamed.
 */
export type StoredChatMessage = {
  sid: string
  body: string
  /** Unix seconds. */
  timestamp: number
  channel_uid: string
  /** Author id; `'support'` for the FAQ bubble. */
  author: string
  attributes?: { type?: string }
}

type ChatMessagesMap = Record<string, StoredChatMessage>

const getChatMessagesStorage = (userId: string) =>
  createLocalStorage<ChatMessagesMap>(
    `kidsout__chat_messages:${userId}`,
    input => {
      try {
        return input ? JSON.parse(input) : {}
      } catch {
        return {}
      }
    },
    value => JSON.stringify(value)
  )

const chatMessagesSlice = createReduxSlice<SliceState<ChatMessagesMap>>('chat_messages')

const chatMessagesRawResultSelector = createSelector([chatMessagesSlice.selector, profileUserResultSelector], (slice, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!IS_BROWSER) return {}
    if (!user) return {}
    return withCheckedSliceState(c, {
      sliceState: slice,
      slice: chatMessagesSlice,
      hash: user.id,
      load: async _dispatch => {
        return getChatMessagesStorage(user.id).get()
      },
    })
  })
)

export const chatMessagesResultSelector = createSelector(
  [chatMessagesRawResultSelector, sitterFaqChatMessageEnabledResultSelector],
  (rawResult, faqEnabledResult) =>
    createLoadable(c => {
      const messages = c.unwrap(rawResult)
      if (c.unwrap(faqEnabledResult)) return messages
      return pickBy(messages, message => message.attributes?.type !== SITTER_FAQ_MESSAGE_TYPE)
    })
)

export const createMessageSelector = (messageSid: string) => mapThunkSelector(chatMessagesResultSelector, messages => messages[messageSid] ?? null)

export const addChatMessage =
  (message: StoredChatMessage): ThunkAction<void> =>
  (dispatch, getState) => {
    const userId = profileUserIdSelector(getState())
    const existing = chatMessagesSlice.selector(getState())?.data ?? {}
    const updatedData = { ...existing, [message.sid]: message }
    dispatch(chatMessagesSlice.update(state => (state ? { ...state, data: updatedData } : state)))
    if (userId) getChatMessagesStorage(userId).set(updatedData)
  }
